import {useCallback, useEffect, useEffectEvent, useState} from 'react';
import type {HttpError} from '../http/types';
import type {DataLoadStatus} from '../types';

export const useGetResourceWithPagination = <TItem>(
  get: (page: number, size: number) => Promise<TItem>,
  size: number,
  page: number,
) => {
  const [result, setResult] = useState<DataLoadStatus<TItem>>({
    status: 'NotLoaded',
  });

  const onSetResult = useEffectEvent((page: number, size: number) => {
    setResult(old => ({status: 'Loading', data: old.data}));
    get(page, size)
      .then(data => {
        setResult(() => ({status: 'Loaded', data}));
      })
      .catch((error: HttpError) => {
        setResult(() => ({status: 'Failed', error}));
      });
  });

  const caller = useCallback(
    (page: number, size: number) => {
      setResult(old => ({status: 'Loading', data: old.data}));
      get(page, size)
        .then(data => {
          setResult(() => ({status: 'Loaded', data}));
        })
        .catch((error: HttpError) => {
          setResult(() => ({status: 'Failed', error}));
        });
    },
    [get],
  );

  useEffect(() => {
    onSetResult(page, size);
  }, [get, page, size]);

  return {
    result,
    refresh: () => caller(page, size),
  };
};
